import React, { useState } from 'react';
import client from '../../api/client';
import usePolling from '../../hooks/usePolling';
import MetricCard from '../common/MetricCard';
import MetricGrid from '../common/MetricGrid';
import InverterControlPanel from '../subsystem/InverterControlPanel';
import StatusBadge from '../common/StatusBadge';
import TimeRangeSelector from '../common/TimeRangeSelector';
import TimeSeriesChart from '../charts/TimeSeriesChart';
import BarChart from '../charts/BarChart';
import LoadingSpinner from '../common/LoadingSpinner';
import colors from '../../theme/colors';

function notifyUpdate() {
  window.dispatchEvent(new Event('sv360:data-update'));
}

export default function InvertersPage() {
  const [inverter, setInverter] = useState('inverter2');
  const [range, setRange] = useState('-1h');

  const { data: latest, loading } = usePolling(async () => {
    const res = await client.get(`/api/inverters/latest?inverter=${inverter}`);
    notifyUpdate();
    return res.data.sections;
  }, 5000, [inverter]);

  const { data: gridLoadHistory } = usePolling(async () => {
    const res = await client.get(`/api/inverters/history?range=${range}&inverter=${inverter}&section=grid&fields=power,frequency`);
    const res2 = await client.get(`/api/inverters/history?range=${range}&inverter=${inverter}&section=load&fields=power,frequency`);
    return {
      grid: res.data.series,
      load: res2.data.series,
    };
  }, 10000, [range, inverter]);

  const { data: battHistory } = usePolling(async () => {
    const res = await client.get(`/api/inverters/history?range=${range}&inverter=${inverter}&section=battery&fields=voltage,current,power`);
    return res.data.series;
  }, 10000, [range, inverter]);

  if (loading && !latest) return <LoadingSpinner />;

  const status = latest?.status || {};
  const battery = latest?.battery || {};
  const grid = latest?.grid || {};
  const load = latest?.load || {};
  const energy = latest?.energy || {};
  const health = latest?.inverter_health || {};

  const energyData = [
    { name: 'PV', value: energy.pv_today || 0, color: '#ffd43b' },
    { name: 'Grid Import', value: energy.grid_import_today || 0, color: '#748ffc' },
    { name: 'Grid Export', value: energy.grid_export_today || 0, color: '#cc5de8' },
    { name: 'Bat Charge', value: energy.battery_charge_today || 0, color: '#51cf66' },
    { name: 'Bat Discharge', value: energy.battery_discharge_today || 0, color: '#ff922b' },
    { name: 'Load', value: energy.load_today || 0, color: '#00d4ff' },
  ];

  return (
    <div>
      {/* Inverter selector */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 16 }}>
        <SelectorBtn label="Inverter 1" active={inverter === 'inverter1'} onClick={() => setInverter('inverter1')} />
        <SelectorBtn label="Inverter 2" active={inverter === 'inverter2'} onClick={() => setInverter('inverter2')} />
      </div>

      {/* Status bar */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
          <StatusBadge label={status.system_state_name || '--'} color={status.system_state === 1 || status.system_state === 2 ? 'green' : status.system_state === 3 ? 'red' : 'yellow'} />
          <StatusBadge label={`Inv: ${status.inverter_state_name || '--'}`} color={status.inverter_state === 3 || status.inverter_state === 4 ? 'green' : status.inverter_state === 5 ? 'red' : 'yellow'} />
          <StatusBadge label={`Bat: ${status.battery_state_name || '--'}`} color="cyan" />
          <StatusBadge label={`Mode: ${status.work_mode_name || '--'}`} color="cyan" />
        </div>
      </div>

      {/* 3-column metrics */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 16 }}>
        <div className="card">
          <div className="card-title">Battery</div>
          <MetricGrid minWidth={100}>
            <MetricCard label="Voltage" value={battery.voltage} unit="V" />
            <MetricCard label="Current" value={battery.current} unit="A" />
            <MetricCard label="Power" value={battery.power} unit="W" decimals={0} />
            <MetricCard label="SoC" value={battery.soc} unit="%" />
          </MetricGrid>
        </div>
        <div className="card">
          <div className="card-title">Grid</div>
          <MetricGrid minWidth={100}>
            <MetricCard label="Frequency" value={grid.frequency} unit="Hz" />
            <MetricCard label="Power" value={grid.power} unit="W" decimals={0} />
            <MetricCard label="R Voltage" value={grid.r_voltage} unit="V" />
            <MetricCard label="S Voltage" value={grid.s_voltage} unit="V" />
            <MetricCard label="T Voltage" value={grid.t_voltage} unit="V" />
          </MetricGrid>
        </div>
        <div className="card">
          <div className="card-title">Load</div>
          <MetricGrid minWidth={100}>
            <MetricCard label="Frequency" value={load.frequency} unit="Hz" />
            <MetricCard label="Power" value={load.power} unit="W" decimals={0} />
            <MetricCard label="R Voltage" value={load.r_voltage} unit="V" />
            <MetricCard label="S Voltage" value={load.s_voltage} unit="V" />
            <MetricCard label="T Voltage" value={load.t_voltage} unit="V" />
          </MetricGrid>
        </div>
      </div>

      {/* Energy + Health row */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
        <div className="card">
          <div className="card-title">Energy Today</div>
          <BarChart data={energyData} unit="kWh" height={220} />
        </div>
        <div className="card">
          <div className="card-title">Inverter Health</div>
          <MetricGrid minWidth={130}>
            <MetricCard label="Temperature" value={health.temperature} unit="°C" />
            <MetricCard label="AC Leakage" value={health.ac_leakage_ma} unit="mA" />
            <MetricCard label="Insulation" value={health.insulation_kohm} unit="kΩ" decimals={0} />
          </MetricGrid>
        </div>
      </div>

      {/* Historical charts */}
      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <span className="card-title" style={{ marginBottom: 0 }}>Grid & Load Power History</span>
          <TimeRangeSelector selected={range} onChange={setRange} />
        </div>
        <TimeSeriesChart
          series={{
            ...(gridLoadHistory?.grid?.power ? { 'Grid Power': gridLoadHistory.grid.power } : {}),
            ...(gridLoadHistory?.load?.power ? { 'Load Power': gridLoadHistory.load.power } : {}),
          }}
          config={{
            height: 300,
            yAxisLabel: 'Power (W)',
            seriesConfig: {
              'Grid Power': { name: 'Grid Power', unit: 'W', color: colors.series[0], decimals: 0 },
              'Load Power': { name: 'Load Power', unit: 'W', color: colors.series[1], decimals: 0 },
            },
          }}
        />
      </div>

      {/* Control Panel */}
      <InverterControlPanel inverter={inverter} />

      <div className="card">
        <div className="card-title">Battery V/I History</div>
        <TimeSeriesChart
          series={battHistory || {}}
          config={{
            height: 300,
            seriesConfig: {
              voltage: { name: 'Voltage', unit: 'V', color: colors.series[0] },
              current: { name: 'Current', unit: 'A', color: colors.series[2], yAxis: 1 },
              power: { name: 'Power', unit: 'W', color: colors.series[1], yAxis: 1, decimals: 0 },
            },
            yAxes: [
              { title: { text: 'Voltage (V)' } },
              { title: { text: 'Current / Power' } },
            ],
          }}
        />
      </div>
    </div>
  );
}

function SelectorBtn({ label, active, disabled, onClick }) {
  return (
    <button onClick={onClick} disabled={disabled} style={{
      padding: '6px 16px',
      borderRadius: 6,
      border: 'none',
      fontSize: 12,
      fontWeight: 600,
      cursor: disabled ? 'not-allowed' : 'pointer',
      background: active ? colors.accent : colors.surface,
      color: active ? colors.bg : colors.textMuted,
      opacity: disabled ? 0.4 : 1,
    }}>
      {label}
    </button>
  );
}

